import React, { useMemo, useRef, useState } from 'react';
import { Clock, Pin, Trash2, Calendar } from 'lucide-react';
import api from '../api';
import { Sheet, ConfirmBox } from './AdminUi';

// Nova / uredi objavu na Oglasnoj ploči — panel u stilu TaskForma: nacrt + Odustani / Spremi.
const TEXT_MAX = 500;
const FINE_POINTER = typeof window !== 'undefined' && window.matchMedia?.('(pointer: fine)').matches;
const pad = (n) => String(n).padStart(2, '0');
const toLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const defaultUntil = () => { const d = new Date(); d.setDate(d.getDate() + 7); d.setHours(20, 0, 0, 0); return toLocal(d); };

function Switch({ on }) {
  return <span className={`tf-sw${on ? ' on' : ''}`} aria-hidden="true" />;
}

function SwitchRow({ icon, label, sub, on, onChange }) {
  return (
    <button type="button" className="tf-row tf-row-btn" onClick={() => onChange(!on)} aria-pressed={on}>
      <span className="tf-ico">{icon}</span>
      <span className="tf-k">{label}{sub && <small>{sub}</small>}</span>
      <Switch on={on} />
    </button>
  );
}

export default function AnnouncementForm({ announcement = null, pinned = null, onClose, onSaved, onRemoved, z = 1100 }) {
  const isEdit = !!announcement;
  const init = useMemo(() => ({
    text: announcement?.text || '',
    limit: !!announcement?.expires_at,
    until: announcement?.expires_at ? toLocal(new Date(announcement.expires_at)) : defaultUntil(),
    highlight: !!announcement?.is_active,
  }), [announcement]);
  const [text, setText] = useState(init.text);
  const [limit, setLimit] = useState(init.limit);
  const [until, setUntil] = useState(init.until);
  const [highlight, setHighlight] = useState(init.highlight);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmDel, setConfirmDel] = useState(false);
  const closeRef = useRef(null);

  const dirty = text !== init.text || limit !== init.limit || (limit && until !== init.until) || highlight !== init.highlight;
  const missing = !text.trim() ? 'Upiši tekst' : limit && !until ? 'Odaberi datum' : limit && new Date(until) <= new Date() ? 'Datum je prošao' : null;
  const otherPinned = pinned && pinned.id_new !== announcement?.id_new ? pinned : null;

  const save = async () => {
    if (missing) return;
    setBusy(true); setError('');
    try {
      const expires_at = limit ? new Date(until).toISOString() : null;
      let saved;
      if (isEdit) {
        await api.put(`/announcement/${announcement.id_new}`, { text, expires_at });
        saved = { ...announcement, text: text.trim(), expires_at };
        if (highlight !== init.highlight) {
          await api.put(`/announcement/${announcement.id_new}/${highlight ? 'activate' : 'deactivate'}`);
          saved.is_active = highlight;
        }
      } else {
        const res = await api.post('/announcement', { text, expires_at });
        saved = res.data;
        if (highlight) {
          await api.put(`/announcement/${saved.id_new}/activate`);
          saved = { ...saved, is_active: true };
        }
      }
      onSaved(saved);
    } catch (err) {
      setError(err.response?.data?.error || 'Spremanje nije uspjelo.');
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true); setError('');
    try {
      await api.delete(`/announcement/${announcement.id_new}`);
      onRemoved(announcement.id_new);
    } catch (err) {
      setError(err.response?.data?.error || 'Brisanje nije uspjelo.');
      setBusy(false);
    }
  };

  return (
    <Sheet z={z} onClose={onClose} dirty={dirty} busy={busy} closeRef={closeRef}
      discardText="Tekst i postavke objave nisu spremljeni i bit će izgubljeni."
      head={
        <div className="adm-sh">
          <div className="adm-main">
            <h3>{isEdit ? 'Uredi objavu' : 'Nova objava'}</h3>
            <p>Vidi je svaki član na Oglasnoj ploči na Početnoj.</p>
          </div>
        </div>
      }
      footer={
        <div className="adm-dirty">
          {error && <p style={{ color: '#ac463b' }}>{error}</p>}
          <div className="tf-foot">
            <button type="button" className="adm-btn" disabled={busy} onClick={() => closeRef.current && closeRef.current()}>Odustani</button>
            <button type="button" className={`adm-btn ${missing ? 'tf-off' : 'primary'}`} disabled={!!missing || busy || (isEdit && !dirty)} onClick={save}>
              {busy ? 'Spremam…' : missing || (isEdit ? 'Spremi promjene' : 'Objavi')}
            </button>
          </div>
        </div>
      }
    >
      <div>
        <textarea id="ann-text" className="adm-input ann-text" value={text} onChange={e => { setText(e.target.value); setError(''); }}
          placeholder="Što članovi trebaju znati?" maxLength={TEXT_MAX} rows={5} autoFocus={!isEdit && FINE_POINTER} />
        <div className={`ann-count${text.length > TEXT_MAX - 50 ? ' warn' : ''}`}>{text.length}/{TEXT_MAX}</div>
      </div>

      <div className="adm-lbl">Trajanje</div>
      <div className="adm-card">
        <SwitchRow icon={<Clock size={17} />} label="Ograniči trajanje"
          sub={limit ? 'Objava nestaje s ploče nakon tog trenutka' : 'Ostaje na ploči dok je ne obrišeš'}
          on={limit} onChange={setLimit} />
        {limit && (
          <label className="tf-row">
            <span className="tf-ico"><Calendar size={17} /></span>
            <span className="tf-k">Vrijedi do</span>
            <input id="ann-until" type="datetime-local" className="tf-dt" value={until} onChange={e => setUntil(e.target.value)} />
          </label>
        )}
      </div>

      <div className="adm-lbl">Isticanje</div>
        <div className="adm-card">
          <SwitchRow icon={<Pin size={17} />} label="Istakni na vrhu Početne"
            sub={otherPinned && highlight ? `Zamjenjuje istaknutu: „${otherPinned.text.length > 40 ? `${otherPinned.text.slice(0, 40)}…` : otherPinned.text}”` : 'Samo jedna objava može biti istaknuta'}
            on={highlight} onChange={setHighlight} />
        </div>
      <p className="adm-note">Spremanjem se ne šalje nikakva obavijest. Ako želiš javiti članovima, na Oglasnoj ploči odaberi ⋯ → Pošalji obavijest.</p>

      {isEdit && (confirmDel ? (
        <ConfirmBox title="Obrisati objavu?" text="Objava nestaje s Oglasne ploče za sve članove."
          onCancel={() => setConfirmDel(false)} onConfirm={remove} busy={busy} />
      ) : (
        <div className="adm-card" style={{ marginTop: 4 }}>
          <button type="button" className="adm-act red" onClick={() => setConfirmDel(true)}><Trash2 size={18} /> Obriši objavu</button>
        </div>
      ))}
    </Sheet>
  );
}
